import { Controller } from "@hotwired/stimulus"
import { logEvent, logWarn } from "../field_logger"

const STORAGE_KEY = "field_user_location"
const DENIED_KEY = "field_geolocation_denied_until"
const DENIAL_TTL_MS = 30 * 60 * 1000 // 30 minutes -- long enough to kill per-navigation
                                       // retry spam, short enough to self-heal if the
                                       // tasker fixes the OS/browser permission without
                                       // ever visiting Settings (card #1900).
let _requesting = false

// Connects to data-controller="field-geolocation"
// Runs on body — requests location once on app load and stores to localStorage.
// Other controllers (e.g. field-places-map) read from localStorage.
export default class extends Controller {
  connect() {
    if (!navigator.geolocation) return

    // Skip if we already have a fresh fix (< 60s old)
    try {
      const cached = JSON.parse(localStorage.getItem(STORAGE_KEY))
      if (cached && (Date.now() - cached.timestamp) < 60_000) return
    } catch { /* ignore */ }

    // Skip the AUTO request if permission was denied recently -- Turbo Drive
    // reconnects this controller on every navigation, and without this marker a
    // denied tasker would re-fire (and log-fail) getCurrentPosition on every page
    // load. This only gates the auto path -- the manual "Enable location" retry
    // in field_settings_controller.js#requestLocation() always attempts live,
    // regardless of this marker.
    try {
      const deniedUntil = Number(localStorage.getItem(DENIED_KEY))
      if (deniedUntil && Date.now() < deniedUntil) return
    } catch { /* ignore */ }

    // Skip if a request is already in flight
    if (_requesting) return
    _requesting = true

    const start = performance.now()
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        _requesting = false
        const elapsed = Math.round(performance.now() - start)
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          timestamp: Date.now()
        }))
        logEvent("geolocation", "Location acquired", {
          accuracy: Math.round(pos.coords.accuracy),
          elapsed
        }).catch(() => {})
      },
      (err) => {
        _requesting = false
        const elapsed = Math.round(performance.now() - start)
        const reason = err.code === err.PERMISSION_DENIED ? "denied"
                     : err.code === err.TIMEOUT           ? "timeout"
                     :                                      "unavailable"
        if (err.code === err.PERMISSION_DENIED) {
          localStorage.setItem(DENIED_KEY, String(Date.now() + DENIAL_TTL_MS))
        }
        logWarn("geolocation", `Location failed: ${reason}`, { code: err.code, elapsed }).catch(() => {})
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
    )
  }

  static getUserLocation() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) }
    catch { return null }
  }
}
